import {FC, useEffect, useRef, useState} from 'react'
import {AdminActivity, AdminActivityType} from '../../services/api'
import {getAdminSocket} from '../../services/adminSocket'

const META: Record<AdminActivityType, {label: string; icon: string; color: string}> = {
  signup:           {label: 'Signup',          icon: 'fa-user-plus',      color: '#6340C8'},
  event_published:  {label: 'Event published', icon: 'fa-calendar-check', color: '#16A34A'},
  ticket_purchased: {label: 'Ticket',          icon: 'fa-ticket',         color: '#F59E0B'},
  service_booked:   {label: 'Booking',         icon: 'fa-briefcase',      color: '#0EA5E9'},
}

const fmtTime = (iso: string): string => {
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit', second: '2-digit'})
}

const MAX_ROWS = 200

const ActivityFeedPage: FC = () => {
  const [items, setItems] = useState<AdminActivity[]>([])
  const [connected, setConnected] = useState(false)
  // Keep the latest list in a ref so the socket handler (bound once) can prepend.
  const itemsRef = useRef<AdminActivity[]>([])

  useEffect(() => {
    const socket = getAdminSocket()
    const onActivity = (a: AdminActivity) => {
      itemsRef.current = [a, ...itemsRef.current].slice(0, MAX_ROWS)
      setItems(itemsRef.current)
    }
    const onConnect = () => setConnected(true)
    const onDisconnect = () => setConnected(false)

    socket.on('activity', onActivity)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)
    setConnected(socket.connected)

    return () => {
      socket.off('activity', onActivity)
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
    }
  }, [])

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6 d-flex align-items-center justify-content-between'>
        <div>
          <h3 className='card-title fw-bold m-0'>Live Activity</h3>
          <span className='text-muted fs-7'>Real-time platform events</span>
        </div>
        <span
          className='badge'
          style={{background: connected ? '#DCFCE7' : '#FEE2E2', color: connected ? '#16A34A' : '#DC2626'}}>
          <i
            className='fas fa-circle me-2'
            style={{fontSize: 8, color: connected ? '#16A34A' : '#DC2626'}}
          />
          {connected ? 'Connected' : 'Disconnected'}
        </span>
      </div>

      <div className='card-body pt-4'>
        {items.length === 0 ? (
          <div className='text-center text-muted py-10'>
            <i className='fas fa-bolt fs-2x mb-3 d-block opacity-50' />
            Waiting for activity… events will stream in live as they happen.
          </div>
        ) : (
          <div className='d-flex flex-column gap-3'>
            {items.map((a, i) => {
              const m = META[a.type] ?? {label: a.type, icon: 'fa-bell', color: '#64748b'}
              return (
                <div key={`${a.at}-${i}`} className='d-flex align-items-center' style={{gap: 14}}>
                  <div
                    style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: `${m.color}1a`, color: m.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                    <i className={`fas ${m.icon}`} style={{color: m.color}} />
                  </div>
                  <div className='flex-grow-1'>
                    <div className='fw-semibold text-gray-900'>{a.title}</div>
                    {a.subtitle ? <div className='text-muted fs-7'>{a.subtitle}</div> : null}
                  </div>
                  <div className='text-muted fs-8'>{fmtTime(a.at)}</div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const ActivityFeedPageWrapper: FC = () => <ActivityFeedPage />

export {ActivityFeedPageWrapper}
